import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, Trophy, X, Save } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Card, Button, Badge, Spinner, PageHeader, Input, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { BranchFilter, PeriodPicker } from "../../components/finance";

export default function HRKpi() {
  const toast = useToast();
  const { user } = useAuth();
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const canInput = ["SUPER_ADMIN", "HR_MANAGER", "BRANCH_MANAGER"].includes(user?.role);
  const [branchId, setBranchId] = useState("");
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [manual, setManual] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/kpi", { params: { period, branch_id: branchId || undefined } }); setData(data); }
    catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [period, branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (empId) => {
    setDetail("loading");
    try { const { data } = await api.get(`/hr/kpi/${empId}`, { params: { period } }); setDetail(data); }
    catch { toast.error("Gagal"); setDetail(null); }
  };

  const openManual = () => {
    const vals = {};
    detail.metrics.filter((m) => m.type === "manual").forEach((m) => { vals[m.key] = m.filled ? m.actual : ""; });
    setManual({ employee_id: detail.employee_id, label: detail.employee_name, defs: detail.metrics.filter((m) => m.type === "manual"), values: vals });
  };

  const saveManual = async () => {
    try {
      const values = {}; Object.entries(manual.values).forEach(([k, v]) => { if (v !== "") values[k] = Number(v); });
      await api.post(`/hr/kpi/${manual.employee_id}/manual`, { period, values });
      toast.success("KPI manual tersimpan"); setManual(null); openDetail(manual.employee_id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="hr-kpi-page">
      <PageHeader title="KPI Karyawan" subtitle="Skor KPI hibrida (otomatis + manual), peringkat & tren" icon={BarChart3}>
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-kpi-branch-filter" />}
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <Card data-testid="hr-kpi-ranking">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-heading font-bold text-ink"><Trophy size={18} /> Peringkat KPI</h3>
            <Badge tone="info">Rata-rata {data.average}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr><th className="py-2 pr-3">#</th><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Posisi</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-right">Skor</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.ranking.map((r) => (
                  <tr key={r.employee_id} data-testid={`hr-kpi-rank-${r.rank}`}>
                    <td className="py-2.5 pr-3 font-bold text-ink">{r.rank}</td>
                    <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-right"><span className={`font-bold ${r.score >= 85 ? "text-success-text" : r.score >= 70 ? "text-warn-text" : "text-danger-text"}`}>{r.score}</span></td>
                    <td className="py-2.5 pr-3 text-right"><Button size="sm" variant="outline" onClick={() => openDetail(r.employee_id)} data-testid={`hr-kpi-detail-${r.rank}`}>Detail</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail drawer */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Detail KPI" size="lg"
        footer={canInput && detail && detail !== "loading" ? <Button onClick={openManual} data-testid="hr-kpi-input-manual">Input KPI Manual</Button> : null}>
        {detail === "loading" ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : detail && (
          <div className="space-y-4" data-testid="hr-kpi-detail">
            <div className="flex items-center justify-between">
              <div><p className="font-heading text-lg font-bold text-ink">{detail.employee_name}</p><p className="text-sm text-ink-mute">{detail.position}</p></div>
              <div className="text-right"><p className="text-xs text-ink-mute">Skor</p><p className="font-heading text-3xl font-bold text-primary">{detail.score}</p></div>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={detail.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip /><Line type="monotone" dataKey="score" stroke="#f97316" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {detail.metrics.map((m) => (
                <div key={m.key} className="rounded-lg border border-line p-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-ink">{m.label} <Badge tone={m.type === "auto" ? "info" : "mute"} className="!text-[10px]">{m.type === "auto" ? "Otomatis" : "Manual"}</Badge></span>
                    <span className="font-bold text-ink">{m.achievement_pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-stone-200"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${Math.min(100, m.achievement_pct)}%` }} /></div>
                  <p className="mt-1 text-xs text-ink-mute">Aktual {m.filled ? `${m.actual}${m.unit}` : "—"} · Target {m.target}{m.unit} · Bobot {m.weight}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Manual input */}
      <Modal open={!!manual} onClose={() => setManual(null)} title="Input KPI Manual" size="md"
        footer={<><Button variant="outline" onClick={() => setManual(null)}>Batal</Button><Button onClick={saveManual} data-testid="hr-kpi-manual-save"><Save size={15} /> Simpan</Button></>}>
        {manual && (
          <div className="space-y-3" data-testid="hr-kpi-manual-form">
            <p className="text-sm text-ink-soft">{manual.label} — nilai 0–120 (%)</p>
            {manual.defs.map((m) => (
              <Input key={m.key} label={`${m.label} (bobot ${m.weight}%)`} type="number" value={manual.values[m.key]}
                onChange={(e) => setManual({ ...manual, values: { ...manual.values, [m.key]: e.target.value } })}
                data-testid={`hr-kpi-manual-${m.key}`} />
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
